<script lang="ts" setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '@/store/user'
import { usePayrollStore } from '@/store/payroll-data'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { useField, useForm } from 'vee-validate'
import { object, array, date } from 'yup'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import {
  DCard,
  DBtn,
  DTextField,
  DMultiselect,
  DModal,
  DLoading
} from 'front-library'
import {
  ReportIncidents,
  generateAccountingProvisions,
  getPayroll,
  getReportRequest
} from '@/app/core/services/reports'
usePayrollChange(() => back())

/* store, router, notifications */
const storePayroll = usePayrollStore()
const store = useUserStore()
const router = useRouter()
const route = useRoute()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Datos */
const itemsPayrolls = ref<any[]>([])
const urlFile = ref(null)
const refresh = ref()

/* Esquema de validacion campos obligatorios y no obligatorios */
const validationSchema = object().shape({
  payrolls: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  startDate: date().required(t('form.valid.required')),
  endDate: date().when('startDate', (startDate: any, schema) => {
    if (startDate[0] !== undefined) {
      return schema
        .min(
          startDate,
          `${t('form.valid.startDateMin')} ${dayjs(startDate).format(
            'DD/MM/YYYY'
          )}`
        )
        .required(t('form.valid.required'))
    }
    return schema.required(t('form.valid.required'))
  })
})

/* Uso del metodo de validación, retorno de errores y metodo para reiniciar formulario */
const { handleSubmit, errors } = useForm<any>({
  initialValues: { translate: 'es' },
  validationSchema
})

/* Definicion campos del formulario */
const { value: payrolls } = useField<string[]>(ref('payrolls'))
const { value: startDate } = useField<string | undefined>('startDate')
const { value: endDate } = useField<string | undefined>('endDate')

/* Metodo que valida los campos obligatorios del formulario */
const onSubmit = handleSubmit((values: any) => {
  const path = route.params.code as string
  const decodeCode = atob(path)
  const createReport: ReportIncidents = {
    code: decodeCode,
    parameters: values
  }
  void generateReport(createReport)
})

/* Crear solicitud del reporte */
const generateReport = async (values: any) => {
  loading.value = true
  messageLoading.value = t('loading.report')
  try {
    const response = await generateAccountingProvisions(values)
    reportRequest(response)
  } catch (error: any) {
    showError(error)
  }
}

/* Mostrar estado de la solicitud del reporte generado */
const reportRequest = (id: string) => {
  urlFile.value = null
  refresh.value = setInterval(async () => {
    try {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        if (response.errors.length === 1) {
          descriptionModal.value = response.errors[0]
        } else {
          for (let i = 0; i < response.errors.length; i++) {
            const error = response.errors[i]
            descriptionModal.value += error
            if (i < response.errors.length - 1) {
              descriptionModal.value += '<br>'
            }
          }
        }
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    } catch (error: any) {
      showError(error)
    }
  }, 5000)
}

/* Validar campos de fechas (no dejar vacios) */
const validateDate = () => {
  if (startDate.value === '') startDate.value = undefined
  if (endDate.value === '') endDate.value = undefined
}

/* Mostrar notificación de error */
const showError = (error: any) => {
  loading.value = false
  notifications.addNotification({
    title: error.message,
    text: error.response?.data?.message || '',
    type: 'error',
    time: 10000
  })
}

/* Cerrar modal */
const closeModal = () => {
  showModal.value = false
}

/* Volver a la lista de reportes */
const back = () => {
  void router.push({ name: 'MainReports' })
}

onMounted(async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    itemsPayrolls.value = await getPayroll(store.user?.id as string)
    payrolls.value = itemsPayrolls.value
      .filter((_: any) => _.id === storePayroll.payroll?.id)
      .map((_: any) => _.id)
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
})

onUnmounted(() => {
  clearInterval(refresh.value)
})
</script>
<template>
  <div class="h-full px-5 pb-5">
    <DLoading v-show="loading" :message="messageLoading" />
    <DModal
      :open="showModal"
      :typeAlert="typeAlertModal"
      :title="titleModal"
      :description="descriptionModal"
      @closeModal="closeModal">
      <template v-slot:description v-if="urlFile != null">
        <a
          class="underline tracking-widest text-blue-600"
          :href="urlFile"
          target="_blank"
          v-html="t('reports.notification.viewFile')" />
      </template>
    </DModal>
    <div class="bg-gray-50 sm:pb-5 lg:pb-12">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="t('reports.accountingProvisions')" />
      </div>
    </div>
    <form class="scroll overflow-auto" style="height: calc(100% - 10rem)">
      <div class="flex flex-col gap-4">
        <DCard :footer="true">
          <div class="grid grid-cols-4">
            <div class="col-span-4 sm:pb-5 lg:pb-0 px-3">
              <div class="grid grid-cols-6 gap-4">
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.payrolls')"
                    v-model="payrolls"
                    :headerTitle="t('reports.fields.payrolls')"
                    :required="true"
                    :options="itemsPayrolls"
                    :error="errors.payrolls" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="t('reports.fields.startDate')"
                    v-model="startDate"
                    :required="true"
                    :error="errors.startDate"
                    @change="validateDate" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="t('reports.fields.endDate')"
                    v-model="endDate"
                    :required="true"
                    :error="errors.endDate"
                    @change="validateDate" />
                </div>
              </div>
            </div>
          </div>
          <template v-slot:footer>
            <div class="flex justify-end gap-2">
              <DBtn
                type="button"
                color="secondary"
                @click="back"
                class="px-5 py-2 font-semibold"
                v-html="t('form.button.back')" />
              <DBtn
                color="excel"
                class="px-5 py-2 font-semibold"
                @click.prevent="onSubmit()"
                v-html="t('form.button.excel')" />
            </div>
          </template>
        </DCard>
      </div>
    </form>
  </div>
</template>
